import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtEur = (v) => `€ ${Number(v || 0).toLocaleString("de-AT", { minimumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("de-AT") : "—");
const today = () => new Date().toISOString().slice(0, 10);

// Anwalts-Sicht: freigegebene UVAs mit Zahlungsanweisung + EPC-QR. KEINE Eigenberechnung.
// runs (optional): kontrollierter Modus (z.B. Dashboard) — kein Eigen-Fetch. onlyOpen: nur offene Zahlungen.
export const LexUvaPanel = ({ showEmpty = false, onlyOpen = false, runs: controlledRuns = null, onChanged }) => {
  const [ownRuns, setOwnRuns] = useState(null);
  const [payingId, setPayingId] = useState(null);
  const [paidDate, setPaidDate] = useState(today());

  const controlled = controlledRuns !== null;
  const load = () => {
    if (controlled) return;
    axios.get(`${API}/lex/uva/current`)
      .then((res) => setOwnRuns(res.data.runs || []))
      .catch(() => setOwnRuns([]));
  };
  useEffect(load, []);

  const markPaid = async (run) => {
    try {
      await axios.post(`${API}/lex/uva/${run.id}/paid`, { paid_date: paidDate });
      toast.success(`UVA ${run.period_label} als bezahlt vermerkt (${new Date(paidDate).toLocaleDateString("de-AT")}).`);
      setPayingId(null);
      if (controlled) onChanged?.(); else load();
    } catch {
      toast.error("Konnte nicht als bezahlt vermerkt werden.");
    }
  };

  let runs = controlled ? controlledRuns : ownRuns;
  if (runs === null) return null;
  if (onlyOpen) runs = runs.filter((r) => r.status === "approved" && r.zahllast > 0);
  if (!runs.length) {
    return showEmpty ? (
      <div className="lex-card p-8" data-testid="lex-uva-empty">
        <p className="text-sm text-slate-500">
          Noch keine freigegebene Voranmeldung. Ihre Kanzlei berechnet und gibt jede UVA für Sie frei —
          sobald das passiert, finden Sie hier Betrag, Zahlungsanweisung und QR-Code.
        </p>
      </div>
    ) : null;
  }

  return (
    <div className="space-y-4" data-testid="lex-uva-panel">
      {runs.map((r) => (
        <div key={r.id} className="lex-card p-6" style={r.status === "approved" ? { borderLeft: "3px solid #C5A059" } : {}}
          data-testid={`lex-uva-run-${r.period}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-widest font-semibold text-slate-500">UVA {r.period_label}</p>
              <p className="lex-mono text-3xl font-semibold mt-1" style={{ color: "#0A1128" }}>{fmtEur(r.zahllast)}</p>
              {r.evz_amount > 0 && (
                <p className="text-xs text-slate-500 lex-mono mt-1" data-testid="lex-uva-evz-breakdown">
                  davon USt {fmtEur(r.ust_zahllast || 0)} + ESt-Vorauszahlung Q{r.evz_quarter}/{r.evz_year} {fmtEur(r.evz_amount)} — eine Überweisung, ein Verwendungszweck
                </p>
              )}
            <p className="text-xs text-slate-500 mt-1">
                {r.status === "paid"
                  ? <>Bezahlt am <span className="lex-mono">{fmtDate(r.paid_at)}</span> ✓</>
                  : r.zahllast <= 0
                    ? <>{r.zahllast < 0 ? "Gutschrift — wird Ihrem Abgabenkonto gutgeschrieben." : "Keine Zahllast."} Keine Zahlung erforderlich ✓</>
                    : <>fällig am <span className="lex-mono font-semibold">{fmtDate(r.due_date)}</span> · von Ihrer Kanzlei geprüft &amp; freigegeben</>}
              </p>
            </div>
            {r.status === "approved" && r.zahllast > 0 && (
              payingId === r.id ? (
                <div className="flex items-end gap-2 flex-wrap" data-testid={`lex-uva-paid-form-${r.period}`}>
                  <div>
                    <label className="block text-[11px] uppercase tracking-widest font-semibold text-slate-400 mb-1">Bezahlt am</label>
                    <input type="date" className="lex-input lex-mono text-sm py-1.5" value={paidDate}
                      max={today()} onChange={(e) => setPaidDate(e.target.value)}
                      data-testid={`lex-uva-paid-date-${r.period}`} />
                  </div>
                  <button onClick={() => markPaid(r)} className="lex-btn text-xs" data-testid={`lex-uva-paid-confirm-${r.period}`}>
                    Bestätigen ✓
                  </button>
                  <button onClick={() => setPayingId(null)}
                    className="text-xs text-slate-400 hover:text-slate-600 underline underline-offset-4 pb-2.5"
                    data-testid={`lex-uva-paid-cancel-${r.period}`}>
                    Abbrechen
                  </button>
                </div>
              ) : (
                <button onClick={() => { setPayingId(r.id); setPaidDate(today()); }} className="lex-btn-outline text-xs"
                  data-testid={`lex-uva-paid-btn-${r.period}`}>
                  Als bezahlt markieren ✓
                </button>
              )
            )}
          </div>
          {r.status === "approved" && r.zahllast > 0 && (
            <div className="mt-5 pt-4 border-t border-slate-100 flex gap-6 flex-wrap items-start">
              <div className="text-sm space-y-1.5 min-w-[240px]">
                <p className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-2">Überweisung an das Finanzamt</p>
                <p><span className="text-slate-500">Empfänger:</span> <span className="font-medium">Finanzamt Österreich</span></p>
                <p><span className="text-slate-500">IBAN:</span> <span className="lex-mono">{r.iban}</span></p>
                <p><span className="text-slate-500">BIC:</span> <span className="lex-mono">{r.bic}</span></p>
                <p><span className="text-slate-500">Verwendungszweck:</span> <span className="lex-mono">{r.reference}</span></p>
                <p className="text-xs text-slate-400 pt-1">Basis {fmtEur(r.basis_netto)} · USt {fmtEur(r.ust)} − Vorsteuerpauschale {fmtEur(r.vstp)}</p>
              </div>
              {r.qr_png_b64 && (
                <div className="text-center">
                  <img src={`data:image/png;base64,${r.qr_png_b64}`} alt="Zahlungs-QR-Code"
                    className="w-36 h-36 border border-slate-200" data-testid={`lex-uva-qr-${r.period}`} />
                  <p className="text-[11px] text-slate-400 mt-1">Mit Banking-App scannen</p>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default LexUvaPanel;
